import { Injectable, inject } from '@angular/core';

import { environment } from '@environment';
import { isInternalEmail } from '@lite-workspace/dto-common';
import { AnalyticsEventName, ANALYTICS_TRACK_TIMEOUT_MS } from './constants/analytics.constants';
import { sanitizeTrackPayload } from './analytics-helpers/sanitize-track-payload';
import { LumirVidService } from './lumir-vid.service';

const ROUND_TRIP_RETURN_KEY = 'analyticsRoundTripReturn';

/** DI-free form of `AnalyticsService.markRoundTripReturn()` for plain modules (photographer `core/bff.ts`). */
export function markAnalyticsRoundTripReturn(): void {
  try {
    sessionStorage.setItem(ROUND_TRIP_RETURN_KEY, location.pathname);
  } catch {
    // Storage off — the return leg just counts as a screen again.
  }
}

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

function readUtmParams(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  }

  return Object.keys(utm).length > 0 ? utm : null;
}

export interface AnalyticsIdentifyPayload {
  profileId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string | null;
  whatsappPhone?: string | null;
  properties?: Record<string, unknown>;
}

function normalizeProfileContact(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  const lowerCaseNormalized = normalized.toLowerCase();
  if (!normalized || lowerCaseNormalized === 'null' || lowerCaseNormalized === 'undefined') return undefined;
  return normalized;
}

function extractProfileContacts(
  payload: Pick<AnalyticsIdentifyPayload, 'phoneNumber' | 'whatsappPhone'>,
  properties: Record<string, unknown>,
): Record<string, string> {
  const phoneNumber = normalizeProfileContact(payload.phoneNumber) ?? normalizeProfileContact(properties['phoneNumber']);
  const whatsappPhone = normalizeProfileContact(payload.whatsappPhone) ?? normalizeProfileContact(properties['whatsappPhone']);

  return {
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(whatsappPhone ? { whatsappPhone } : {}),
  };
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private opPromise: Promise<import('@openpanel/web').OpenPanel> | null = null;
  private op: import('@openpanel/web').OpenPanel | null = null;
  private isExcluded = false;
  private transmissionOpened = false;
  private firstScreenViewSeen = false;
  private readonly counters = new Map<string, number>();
  private readonly lumirVidService = inject(LumirVidService);

  constructor() {
    const config = environment.openPanel;
    if (config) {
      this.opPromise = import('@openpanel/web').then(({ OpenPanel }) => {
        const op = new OpenPanel({
          clientId: config.clientId,
          apiUrl: config.apiUrl,
          disabled: true,
          trackScreenViews: true,
          trackOutgoingLinks: true,
          trackAttributes: true,
          filter: (payload): boolean => {
            if (this.isExcluded) return false;
            if (payload.type === 'track' && payload.payload.name === 'screen_view' && this.isRoundTripReturn()) return false;
            sanitizeTrackPayload(payload);
            return true;
          },
          // Replay records everything unmasked (inputs, text, canvas) — product decision; blockSelector undefined keeps the SDK's
          // [data-openpanel-replay-block], the one cut-out (the one-time login code).
          sessionReplay: {
            enabled: true,
            maskAllInputs: false,
            maskAllText: false,
            blockSelector: undefined,
            blockClass: '',
            ignoreSelector: undefined,
          },
        });

        const globals: Record<string, unknown> = {};
        const utm = readUtmParams();
        if (utm) Object.assign(globals, utm);
        const lumirVid = this.lumirVidService.resolve();
        if (lumirVid) globals['lumir_vid'] = lumirVid;
        op.setGlobalProperties(globals);
        // Transmission stays closed (events queue) until either the app layer declares the session
        // anonymous (no auth token → cannot be internal) or identify() confirms a non-internal user.
        // An internal identify never opens it, so the queued pre-identify events die unsent.
        this.op = op;

        return op;
      });
      this.registerUnloadFlush();
    }
  }

  /**
   * Call right before a full-page round trip (SSO bridge) that returns to this same page: the returning
   * document is the same screen, so its load-time screen_view is dropped. One-shot per tab.
   */
  markRoundTripReturn(): void {
    markAnalyticsRoundTripReturn();
  }

  /** Only the document's first screen_view can be the return leg; the mark is spent either way. */
  private isRoundTripReturn(): boolean {
    if (this.firstScreenViewSeen) return false;
    this.firstScreenViewSeen = true;
    try {
      const path = sessionStorage.getItem(ROUND_TRIP_RETURN_KEY);
      sessionStorage.removeItem(ROUND_TRIP_RETURN_KEY);
      return path === location.pathname;
    } catch {
      return false;
    }
  }

  /** A session without an auth token can never be internal — safe to transmit from page load. */
  openAnonymousTransmission(): void {
    this.opPromise?.then(op => this.openTransmission(op)).catch(() => {});
  }

  private openTransmission(op: import('@openpanel/web').OpenPanel): void {
    if (this.transmissionOpened) return;
    this.transmissionOpened = true;
    op.ready();
  }

  private registerUnloadFlush(): void {
    if (typeof window === 'undefined') return;
    const flushQueue = (): void => this.op?.flush();
    window.addEventListener('pagehide', flushQueue);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushQueue();
    });
  }

  track(name: AnalyticsEventName, properties?: Record<string, unknown>): void {
    if (this.shouldExcludeByEmail(properties)) {
      return;
    }

    this.opPromise?.then(op => op.track(name, properties)).catch(() => {});
  }

  async trackAndWait(name: AnalyticsEventName, properties?: Record<string, unknown>): Promise<void> {
    if (this.shouldExcludeByEmail(properties)) {
      return;
    }

    const opPromise: Promise<import('@openpanel/web').OpenPanel> | null = this.opPromise;
    if (!opPromise) {
      return;
    }

    const trackPromise: Promise<void> = opPromise
      .then(async op => {
        await op.track(name, properties);
      })
      .catch(() => {});

    const timeoutPromise: Promise<void> = new Promise(resolve => {
      setTimeout(resolve, ANALYTICS_TRACK_TIMEOUT_MS);
    });

    await Promise.race([trackPromise, timeoutPromise]);
  }

  identify(payload: AnalyticsIdentifyPayload): void {
    void this.identifyAndWait(payload).catch(() => {});
  }

  /**
   * The exclusion half of identify() alone: an internal email stops every later event and replay chunk,
   * and nothing reaches the SDK either way — no profile, no id, no transmission change.
   */
  excludeIfInternal(email: string | null | undefined): boolean {
    if (!isInternalEmail(email)) return false;
    this.isExcluded = true;
    return true;
  }

  async identifyAndWait(payload: AnalyticsIdentifyPayload): Promise<void> {
    if (this.excludeIfInternal(payload.email)) {
      return;
    }

    const { phoneNumber, whatsappPhone, properties, ...identityPayload } = payload;
    const baseProperties: Record<string, unknown> = { ...(properties ?? {}) };
    delete baseProperties['phoneNumber'];
    delete baseProperties['whatsappPhone'];
    const profileContacts = extractProfileContacts({ phoneNumber, whatsappPhone }, properties ?? {});
    const lumirVid: string | null = this.lumirVidService.resolve();
    const enrichedProperties: Record<string, unknown> = {
      ...baseProperties,
      ...profileContacts,
      ...(lumirVid ? { lumir_vid: lumirVid } : {}),
    };

    const opPromise: Promise<import('@openpanel/web').OpenPanel> | null = this.opPromise;
    if (!opPromise) {
      return;
    }

    // Gateway maps a null email to '' — OpenPanel stores '' as a real profile value.
    const identity = Object.fromEntries(
      Object.entries(identityPayload).filter(([, value]) => value !== ''),
    ) as typeof identityPayload;
    const op: import('@openpanel/web').OpenPanel = await opPromise;
    op.identify({ ...identity, properties: enrichedProperties });
    this.openTransmission(op);
  }

  /**
   * Id назначает СЕРВЕР — SDK берёт его из ответа на первый `/track`, поэтому пусто, пока передача
   * закрыта или ответ не пришёл: бутстрап-запросы уходят без сессии, в приложении входа передача до
   * успешного identify не открывается вовсе, а под аккаунтом из EXCLUDED_EMAILS и при адблоке id не
   * появляется никогда. Пустая выдача поиска по сессии под таким аккаунтом — не дефект связки.
   */
  getSessionId(): string | undefined {
    return this.op?.getSessionId() || undefined;
  }

  setGlobalProperties(props: Record<string, unknown>): void {
    this.opPromise?.then(op => op.setGlobalProperties(props)).catch(() => {});
  }

  /**
   * In-memory per-key counter scoped to this service instance.
   * Returns the new value after incrementing.
   */
  increment(key: string): number {
    const next = (this.counters.get(key) ?? 0) + 1;
    this.counters.set(key, next);
    return next;
  }

  clear(): void {
    this.isExcluded = false;
    this.counters.clear();
    this.opPromise?.then(op => op.clear()).catch(() => {});
  }

  private shouldExcludeByEmail(properties?: Record<string, unknown>): boolean {
    const rawEmail: unknown = properties?.['email'];
    const emailString: string | undefined = typeof rawEmail === 'string' ? rawEmail : undefined;
    return this.isExcluded || isInternalEmail(emailString);
  }
}
